// City / state → lat-lng lookup for plotting provider locations on a map.
// provider_locations stores only text addresses (no coordinates), so we
// geocode at the city level from a static dictionary. When a city is not
// known (or the address is junk), we fall back to the state centroid so the
// location still shows up on the map. Precision is reported so the UI can
// hint when a marker is only approximate.

const normalize = (s) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    // eslint-disable-next-line no-misleading-character-class
    .replace(/[̀-ͯ]/g, "") // strip accents
    .replace(/\s+/g, " ");

// Centroid (approx.) for each of the 32 Mexican states — fallback only.
export const STATE_CENTROIDS = {
  "aguascalientes": [21.8853, -102.2916],
  "baja california": [30.8406, -115.2838],
  "baja california sur": [26.0444, -111.6661],
  "campeche": [19.8301, -90.5349],
  "chiapas": [16.7569, -93.1292],
  "chihuahua": [28.633, -106.0691],
  "ciudad de mexico": [19.4326, -99.1332],
  "coahuila": [27.0587, -101.7068],
  "colima": [19.1223, -104.0072],
  "durango": [24.0277, -104.6532],
  "estado de mexico": [19.4969, -99.7233],
  "guanajuato": [21.019, -101.2574],
  "guerrero": [17.4392, -99.5451],
  "hidalgo": [20.0911, -98.7624],
  "jalisco": [20.6595, -103.3494],
  "michoacan": [19.5665, -101.7068],
  "morelos": [18.6813, -99.1013],
  "nayarit": [21.7514, -104.8455],
  "nuevo leon": [25.5922, -99.9962],
  "oaxaca": [17.0732, -96.7266],
  "puebla": [19.0414, -98.2063],
  "queretaro": [20.5888, -100.3899],
  "quintana roo": [19.1817, -88.4791],
  "san luis potosi": [22.1565, -100.9855],
  "sinaloa": [24.8091, -107.394],
  "sonora": [29.2972, -110.3309],
  "tabasco": [17.8409, -92.6189],
  "tamaulipas": [24.2669, -98.8363],
  "tlaxcala": [19.3139, -98.2404],
  "veracruz": [19.1738, -96.1342],
  "yucatan": [20.7099, -89.0943],
  "zacatecas": [22.7709, -102.5832],
};

// City → lat-lng. Keyed by normalized city name; covers every city currently
// present in provider_locations plus common aliases. Add rows here as new
// provider cities appear.
export const CITY_COORDS = {
  "colima": [19.2433, -103.725],
  "guadalupe": [25.6767, -100.2563], // Guadalupe, N.L.
  "tlanepantla de baz": [19.54, -99.1949],
  "tlanepantla": [19.54, -99.1949],
  "tlalnepantla": [19.54, -99.1949],
  "tlalnepantla de baz": [19.54, -99.1949],
  "tonala": [20.6247, -103.2336], // Tonalá, Jalisco
  "monterrey": [25.6866, -100.3161],
  "jiutepec": [18.8814, -99.1786],
  "hermosillo": [29.0729, -110.9559],
  "san pedro garza garcia": [25.658, -100.402],
  "victoria de durango": [24.0277, -104.6532],
  "durango": [24.0277, -104.6532],
  "queretaro": [20.5888, -100.3899],
  "santiago de queretaro": [20.5888, -100.3899],
  "culiacan": [24.8091, -107.394],
  "coatzacoalcos": [18.1345, -94.4587],
  "iztapalapa": [19.3574, -99.0895],
  "apizaco": [19.4126, -98.14],
  "villahermosa": [17.9892, -92.9475],
  "azcapotzalco": [19.4833, -99.1836],
  "guadalajara": [20.6597, -103.3496],
  "xalapa": [19.5438, -96.9102],
  "nuevo laredo": [27.4763, -99.5164],
  "puebla": [19.0414, -98.2063],
  "progreso": [21.2833, -89.6667], // Progreso, Yucatán
  "tetla de la solidaridad": [19.55, -98.12],
  "victoria": [23.7369, -99.1411], // Ciudad Victoria, Tamaulipas
  "ciudad victoria": [23.7369, -99.1411],
  "saltillo": [25.4232, -101.0053],
  "cancun": [21.1619, -86.8515],
  "cuauhtemoc": [28.405, -106.8667], // Cuauhtémoc, Chihuahua
  "chihuahua": [28.6353, -106.0889],
  "juarez": [31.6904, -106.4245], // Ciudad Juárez
  "ciudad juarez": [31.6904, -106.4245],
  "delicias": [28.19, -105.4706],
  "nuevo casas grandes": [30.4167, -107.9114],
  "hidalgo del parral": [26.9333, -105.6667],
  "xicontepec de juarez": [20.2747, -97.9636],
  "xicotepec": [20.2747, -97.9636],
  "san nicolas de los garza": [25.7417, -100.3025],
  "ensenada": [31.8667, -116.5964],
  "escobedo": [25.7933, -100.3164],
  "general escobedo": [25.7933, -100.3164],
  "anahuac": [27.2372, -100.1508], // Anáhuac, N.L.
};

// Resolve a location to coordinates. Returns { lat, lng, precision } where
// precision is "city" (exact-ish) or "state" (approximate fallback), or null
// when neither the city nor the state can be resolved.
export function geocodeLocation({ city, state }) {
  const c = normalize(city);
  if (c && CITY_COORDS[c]) {
    const [lat, lng] = CITY_COORDS[c];
    return { lat, lng, precision: "city" };
  }
  const s = normalize(state);
  if (s && STATE_CENTROIDS[s]) {
    const [lat, lng] = STATE_CENTROIDS[s];
    return { lat, lng, precision: "state" };
  }
  return null;
}
